import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { FieldLabel } from '@/components/FieldLabel'
import { ObjectPicker } from '@/components/ObjectPicker'
import { RetailLayoutField } from '@/components/RetailLayoutField'
import { useRetailCreativeCreation } from '@/hooks/useRetailCreativeCreation'
import { type LineItem } from '@/services/lineItems/lineItemsApi'
import { searchLineItems } from '@/services/lineItems/lineItemSearch'
import type { Merchant, RetailProduct } from '@/services/productFeed/productFeedApi'

interface RetailCreativesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  products: RetailProduct[]
  merchants: Merchant[]
  /** Called once every product has a creative, e.g. to clear the selection. */
  onCreated: () => void
}

/** Creates a creative for each selected retail product on a line item picked here. */
export function RetailCreativesDialog({
  open,
  onOpenChange,
  products,
  merchants,
  onCreated,
}: RetailCreativesDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [lineItem, setLineItem] = useState<LineItem>()
  const [openAfter, setOpenAfter] = useState(true)
  const creation = useRetailCreativeCreation(lineItem?.id, open && !!lineItem)

  const canCreate = !!lineItem && !!creation.layoutId && products.length > 0 && !creation.creating

  const create = async () => {
    if (!lineItem) return
    const result = await creation.create(lineItem.id, products, merchants)
    if (result.failures.length > 0) return
    onCreated()
    onOpenChange(false)
    if (openAfter) navigate(`/advertising/lineitems/${lineItem.id}`)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !creation.creating && onOpenChange(next)}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>{t('retailProducts.createTitle')}</DialogTitle>
          <DialogDescription>
            {t('retailProducts.createDescription', { count: products.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('retailProducts.lineItem')}</FieldLabel>
            {/* One line item at most: picking another replaces it. */}
            <ObjectPicker<LineItem>
              queryKey="retail-line-item-search"
              search={searchLineItems}
              selected={lineItem ? [lineItem] : []}
              onChange={(next) => setLineItem(next.at(-1))}
              addLabel={t('retailProducts.pickLineItem')}
              searchPlaceholder={t('retailProducts.searchLineItem')}
              emptyText={t('retailProducts.noLineItem')}
              renderMeta={(li) => [li.advertiserName, li.orderName].filter(Boolean).join(' · ')}
              disabled={creation.creating}
            />
          </div>

          {lineItem && (
            <RetailLayoutField
              layouts={creation.layouts}
              loading={creation.layoutsLoading}
              value={creation.layoutId}
              onChange={creation.setLayoutId}
              disabled={creation.creating}
            />
          )}

          {lineItem && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="retail-creatives-open"
                checked={openAfter}
                onCheckedChange={(v) => setOpenAfter(v === true)}
              />
              <Label htmlFor="retail-creatives-open" className="font-normal">
                {t('retailProducts.openLineItemAfter')}
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={creation.creating}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!canCreate} onClick={create}>
            {creation.creating && <Loader2 className="size-4 animate-spin" />}
            {t('retailProducts.createCreatives', { count: products.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
